// Renderer del Ticket POS 80mm.
//
// La misma función se usa desde:
// - lib/printTicket80.js (impresión real)
// - pages/AdminLabelsPage (preview en vivo)
//
// Toma un `pedido` (real o de ejemplo) y un `config` (deep-merged con defaults
// por el backend) y devuelve el HTML completo del ticket.

const esc = (v) => {
  if (v === null || v === undefined) return "";
  return String(v)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
};

const money = (n) =>
  new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
  }).format(Number(n || 0));

/**
 * Datos de ejemplo que usa el preview del admin.
 */
export const SAMPLE_PEDIDO = {
  numero: "#0001",
  cliente_nombre: "Juan Pérez",
  cliente_cuit: "20-12345678-9",
  cliente_telefono: "+54 9 341 555-1234",
  direccion: "Av. Corrientes 1234",
  direccion_piso: "5",
  direccion_dpto: "B",
  direccion_timbre: null,
  localidad: "Rosario",
  items: [
    { name: "Asado de tira", quantity: 2, unidad: "kg", price: 8500, especificacion: "Corte fino" },
    { name: "Chorizo criollo", quantity: 6, unidad: "u", price: 1200 },
    { name: "Vacío", quantity: 1, unidad: "kg", price: 9800, especificacion: "Churrasco" },
  ],
  subtotal: 34000,
  envio: 2000,
  descuento: 1500,
  total: 34500,
  metodo_pago: "Efectivo",
  observaciones: "Sin sal, envolver en papel manteca.",
};

/**
 * @param {object} pedido
 * @param {object} config — objeto con textos/secciones/fuentes/layout resuelto
 * @param {{autoprint?: boolean, logo?: string, empresa?: object, preview?: boolean, modificacion?: boolean}} opts
 */
export function renderPosTicket80Html(pedido, config, opts = {}) {
  const { autoprint = false, logo = "", empresa = {}, preview = false, modificacion = false } = opts;
  const textos = config?.textos || {};
  const sec = config?.secciones || {};
  const fnt = config?.fuentes || {};
  const layout = config?.layout || {};
  const anchoMm = layout.ancho_mm ?? 72;
  const margenMm = layout.margen_mm ?? 2;

  const fecha = new Date().toLocaleString("es-AR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });

  const items = (pedido.items || []).map((it) => {
    const qty = Number(it.quantity ?? it.cantidad ?? 1);
    const price = Number(it.price ?? it.precio_unitario ?? 0);
    const desc = it.name || it.descripcion || "";
    const unidad = it.unidad || "";
    const espec = (it.especificacion || "").trim();
    return { qty, price, desc, unidad, especificacion: espec, importe: qty * price };
  });

  const dirParts = [
    pedido.direccion,
    pedido.direccion_piso ? `Piso ${pedido.direccion_piso}` : "",
    pedido.direccion_dpto ? `Dpto ${pedido.direccion_dpto}` : "",
    pedido.direccion_timbre ? `Timbre ${pedido.direccion_timbre}` : "",
    pedido.localidad,
  ].filter(Boolean).join(" · ");

  const showLogo = sec.logo !== false && !!logo;
  const showFechaCab = sec.fecha_cabecera !== false;
  const showCuit = sec.cuit_cliente !== false && pedido.cliente_cuit;
  const showTel = sec.telefono_cliente !== false && pedido.cliente_telefono;
  const showDir = sec.direccion_cliente !== false && dirParts;
  const showSubtotal = sec.subtotal !== false && Number(pedido.subtotal || 0) && Number(pedido.subtotal || 0) !== Number(pedido.total || 0);
  const showEnvio = sec.envio !== false && Number(pedido.envio || 0);
  const showDescuento = sec.descuento !== false && Number(pedido.descuento || 0);
  const showPago = sec.metodo_pago !== false && pedido.metodo_pago;
  const showObs = sec.observaciones !== false && pedido.observaciones;
  const showTotal = sec.total !== false;
  const showFooter = sec.footer !== false && (textos.footer || "");

  const style = `
    @page { size: ${anchoMm + margenMm * 2}mm auto; margin: 0; }
    * { box-sizing: border-box; }
    html, body {
      width: ${anchoMm}mm;
      margin: 0;
      padding: ${margenMm}mm ${margenMm}mm ${margenMm * 2}mm ${margenMm}mm;
      font-family: 'Arial Black', 'Helvetica', Arial, sans-serif;
      font-size: ${fnt.base_px ?? 13}px;
      font-weight: 900;
      line-height: 1.3;
      color: #000;
      -webkit-font-smoothing: none;
      -webkit-text-stroke: 0.35px #000;
      ${preview ? "background: #fff;" : ""}
    }
    .center { text-align: center; }
    .right { text-align: right; }
    .bold { font-weight: 900; }
    .titulo { font-size: ${fnt.titulo_px ?? 17}px; }
    .cliente { font-size: ${fnt.cliente_px ?? 15}px; }
    .fecha-cab { font-size: ${Math.max(11, (fnt.titulo_px ?? 17) - 2)}px; }
    .divider { border: none; border-top: 2px solid #000; margin: 5px 0; }
    .logo { max-width: ${anchoMm - 15}mm; max-height: 18mm; margin: 0 auto 3px; display: block; filter: contrast(1.4) brightness(0.85); }
    table { width: 100%; border-collapse: collapse; }
    th, td { padding: 3px 0; vertical-align: top; }
    th { border-bottom: 2px solid #000; text-align: left; font-size: ${(fnt.items_px ?? 15) - 1}px; font-weight: 900; }
    th.n, td.n { text-align: right; }
    .items td { font-size: ${fnt.items_px ?? 15}px; font-weight: 900; padding: 4px 0; border-bottom: 1px dashed #000; }
    .items td.qty { width: 16mm; text-align: right; padding-right: 4mm; white-space: nowrap; }
    .items td.desc { word-wrap: break-word; overflow-wrap: anywhere; text-transform: uppercase; }
    .items td.desc .espec {
      display: block;
      margin-top: 2px;
      font-size: ${Math.max(11, (fnt.items_px ?? 15) - 2)}px;
      font-weight: 900;
      font-style: italic;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }
    .totals td { padding: 3px 0; font-size: ${fnt.totales_px ?? 15}px; font-weight: 900; }
    .totals .lbl { text-align: right; padding-right: 6px; }
    .totals .val { text-align: right; }
    .total-final { font-size: ${fnt.total_final_px ?? 22}px; }
    .box { border: 2px solid #000; padding: 4px 5px; margin-top: 4px; border-radius: 2px; font-weight: 900; }
    .obs { white-space: pre-wrap; }
    .row { display: flex; justify-content: space-between; gap: 4px; }
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  `;

  const script = autoprint ? `
    <script>
      window.addEventListener('load', function () {
        setTimeout(function () { window.focus(); window.print(); }, 120);
      });
    </script>
  ` : "";

  return `<!DOCTYPE html>
<html><head>
<meta charset="utf-8" />
<title>Ticket ${esc(pedido.numero)}</title>
<style>${style}</style>
</head>
<body>
  ${showLogo ? `<img class="logo" src="${esc(logo)}" alt="logo" />` : ""}
  ${modificacion ? `<div class="bold" style="text-align:center;padding:4px 0;margin-bottom:4px;border:3px solid #000;font-size:${(fnt.titulo_px ?? 17) + 2}px;letter-spacing:1px;">*** MODIFICACION DE PEDIDO ***</div>` : ""}
  <div class="row bold">
    <div class="titulo">${esc(textos.titulo_pedido || "PEDIDO")} ${esc(pedido.numero || "")}</div>
    ${showFechaCab ? `<div class="fecha-cab">${esc(fecha)}</div>` : ""}
  </div>
  ${textos.subtitulo ? `<div class="bold">${esc(textos.subtitulo)}</div>` : ""}
  <hr class="divider" />
  ${textos.label_cliente ? `<div class="bold cliente">${esc(textos.label_cliente)}</div>` : ""}
  <div class="bold cliente">${esc(pedido.cliente_nombre || "Consumidor Final")}</div>
  ${showCuit ? `<div>${esc(textos.label_cuit || "CUIT/DNI:")} ${esc(pedido.cliente_cuit)}</div>` : ""}
  ${showTel ? `<div>${esc(textos.label_tel || "Tel:")} ${esc(pedido.cliente_telefono)}</div>` : ""}
  ${showDir ? `<div>${esc(dirParts)}</div>` : ""}
  <hr class="divider" />
  <table class="items">
    <thead>
      <tr>
        <th class="n" style="width:18mm">${esc(textos.col_cantidad || "Cant.")}</th>
        <th>${esc(textos.col_detalle || "Detalle")}</th>
      </tr>
    </thead>
    <tbody>
      ${items.map((it) => `
        <tr>
          <td class="qty">${esc(String(it.qty))}${it.unidad ? ` ${esc(it.unidad)}` : ""}</td>
          <td class="desc">
            ${esc(it.desc)}
            ${it.especificacion ? `<div class="espec">▸ ${esc(it.especificacion)}</div>` : ""}
          </td>
        </tr>
      `).join("")}
    </tbody>
  </table>
  <hr class="divider" />
  ${(showSubtotal || showEnvio || showDescuento || showTotal) ? `
  <table class="totals">
    ${showSubtotal ? `<tr><td class="lbl">${esc(textos.label_subtotal || "Subtotal:")}</td><td class="val">${esc(money(pedido.subtotal))}</td></tr>` : ""}
    ${showEnvio ? `<tr><td class="lbl">${esc(textos.label_envio || "Envío:")}</td><td class="val">${esc(money(pedido.envio))}</td></tr>` : ""}
    ${showDescuento ? `<tr><td class="lbl">${esc(textos.label_descuento || "Descuento:")}</td><td class="val">- ${esc(money(pedido.descuento))}</td></tr>` : ""}
    ${showTotal ? `<tr><td class="lbl bold total-final">${esc(textos.label_total || "TOTAL:")}</td><td class="val bold total-final">${esc(money(pedido.total || 0))}</td></tr>` : ""}
  </table>
  ` : ""}
  ${showPago ? `<div class="box"><span class="bold">${esc(textos.label_pago || "Pago:")}</span> ${esc(pedido.metodo_pago)}</div>` : ""}
  ${showObs ? `<div class="box obs"><span class="bold">${esc(textos.label_obs || "Obs:")}</span> ${esc(pedido.observaciones)}</div>` : ""}
  <hr class="divider" />
  ${showFooter ? `<div class="center">${esc(textos.footer || "¡Gracias por su compra!")}</div>` : ""}
  <div style="height: 6mm"></div>
  ${script}
</body></html>`;
}
